import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import { AvatarGroup, Button, Checkbox, Collapse, FormControlLabel, Grid2, InputAdornment, Link, List, ListItem, ListItemButton, ListItemText, ListSubheader, OutlinedInput, Paper, SpeedDial, SpeedDialAction, SpeedDialIcon, Tab, Tabs } from "@mui/material";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import _ from "lodash";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { isCourseActive, isSamePageLinkNavigation } from '../common/util';
import { DialogsContext, SessionDataContext } from '../contexts';
import ActivityTable from '../enrollment/activity.table';
import EnrollmentCard from "../enrollment/enrollment.card";
import StudentAvatar from '../student/student.avatar';
import SubjectName from "../subject/subject.name";
import CourseCard from "./course.card";
import { courseApi } from './course.queries';
import CourseDetailTable from "./courseDetail.table";

export default function CourseView(props) {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	let { id, navTab, enrollmentId } = useParams();
	const useGet = courseApi.useGet(id);
	const course = useGet?.data;

	const useGetAll = courseApi.useGetAll();
	let courses = useGetAll?.data;

	const { dialogs } = useContext(DialogsContext);
	const sessionData = useContext(SessionDataContext);

	const [expand, setExpand] = useState({});
	const [tab, setTab] = useState(navTab == 'enrollments' || enrollmentId ? 'enrollments' : 'details');
	const [search, setSearch] = useState('');
	const [hideInactive, setHideInactive] = useState(sessionData.hideInactive);

	useEffect(() => {
		if (searchParams.has('new') && course) {
			dialogs.open('courseDetailWizard', { course, courseId: course.id });
			searchParams.delete('new');
        	setSearchParams(searchParams, { replace: true });
		}
	}, [searchParams, course]);

	const toggle = (key) => {
		expand[key] = !expand[key];
		setExpand({ ...expand });
	}

	const handleNav = (event) => {
		if (isSamePageLinkNavigation(event)) {
			event.preventDefault();
			const href = event.currentTarget.href;
			window.history.pushState({ urlPath: href }, '', href);
		}
	};

	if (!course) {
		return;
	}

	courses = _.orderBy(courses, ['subject.name', 'name']);
	courses = _.filter(courses, c => !search || c.name.toLowerCase().indexOf(search.toLowerCase()) >= 0);

	const groups = _.groupBy(courses, 'subjectId');
	const spacingSx = { ml: -2, mr: -3.75 };
	const enrollment = enrollmentId ? _.find(course?.enrollments, { id: enrollmentId }) : course?.enrollments?.length == 1 ? course.enrollments[0] : null;
	if (enrollment || enrollmentId) {
		enrollmentId = (enrollment?.id ? enrollment.id : enrollmentId);
		//Only enrollment, update the URL accordingly
		let href = window.location.href;
		if (href.indexOf('enrollments') > 0) {
			if (href.indexOf(enrollmentId) < 0) {
				href += '/' + enrollmentId;
				window.history.pushState({ urlPath: href }, '', href);
			}
		}
		else if (href.indexOf('details') < 0) {
			href += '/enrollments/' + enrollmentId;
			window.history.pushState({ urlPath: href }, '', href);
			setTab('enrollments');
		}

		if( enrollment ){
			enrollment.course = _.pick(course, ['id', 'name']);
		}
	}

	return (
		<Stack sx={{ p: 2, pb: 8 }}>
			<Stack direction="column" spacing={1}>
				<List component="nav" sx={{ pr: 2 }} dense={true}>
					<ListItem>
						<Stack direction="column">
							<OutlinedInput sx={{ minWidth: 225 }} startAdornment={
								<InputAdornment position="start">
									<SearchIcon />
								</InputAdornment>
							} defaultValue={search} onChange={(e) => setSearch(e.target.value)} />
							<FormControlLabel sx={{ mt: 0 }} control={<Checkbox checked={hideInactive} onChange={() => { sessionData.set('hideInactive', !hideInactive); setHideInactive(!hideInactive); }} />} label="Hide Inactive?" />
						</Stack>
					</ListItem>
					<ListSubheader sx={spacingSx} component="div">
						<Link onClick={() => navigate('/courses')}>
							<Typography variant="subtitle1" sx={{ ml: 8, py: 2, fontWeight: 600, fontVariant: 'small-caps' }}>Courses</Typography>
						</Link>
					</ListSubheader>
					{groups && Object.keys(groups).map((key) => {
						const selected = course.subject?.id == key;
						return (
							<React.Fragment key={key}>
								<ListItemButton sx={spacingSx} selected={selected} onClick={() => toggle(key)}>
									<SubjectName subject={groups[key][0].subject} sx={{ mr: 'auto' }} size={24} />
									{!selected && (expand[key] ? <ExpandLess /> : <ExpandMore />)}
								</ListItemButton>
								<Collapse in={selected || expand[key]}>
									<List component="div" disablePadding dense>
										{groups[key].map((otherCourse) => (!hideInactive || isCourseActive(otherCourse)) && (
											<ListItemButton sx={[spacingSx, { pl: 12 }]} component="a" onClick={() => navigate('/courses/' + otherCourse.id)} key={otherCourse.id} selected={course.id == otherCourse.id}>
												<ListItemText primary={otherCourse.name} />
												<AvatarGroup max={3} sx={{ ml: 2 }}>
													{otherCourse.enrollments?.map((enrollment) => (
														<StudentAvatar key={enrollment.id} student={enrollment.student} enrollment={enrollment} />
													))}
												</AvatarGroup>
											</ListItemButton>
										))}
									</List>
								</Collapse>
							</React.Fragment>
						)
					})}
				</List>
			</Stack>

			<Paper sx={{ p: 2, flexGrow: 1 }}>
				<Grid2 container>
					<Grid2 size={{ xs: 12, md: 6, lg: 4, xl: 3 }}>
						<CourseCard course={course} showAttachments={true} />
					</Grid2>
					<Grid2 size={{ xs: 12, md: 6, lg: 4, xl: 3 }} offset={{ xs: 0, lg: 3, xl: 6 }} >
						<EnrollmentCard enrollment={enrollment} showAttachments={true} />
					</Grid2>

					<Grid2 size={{ xs: 12 }} sx={{}}>
						<Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
							<Tabs textColor="secondary" indicatorColor="secondary" variant="fullWidth" value={tab} onChange={(e, value) => setTab(value)}>
								<Tab label="Coursework" value="details" href={'/courses/' + course.id + '/details'} onClick={(e) => handleNav(e)} />
								<Tab label={enrollment ? 'Enrollment: ' + enrollment.student?.name : 'Enrollments'} value="enrollments" href={'/courses/' + course.id + '/enrollments'} onClick={(e) => handleNav(e)} />
							</Tabs>
						</Box>
						{tab == 'details' &&
							<Box>
								{course.courseDetails &&
									<CourseDetailTable course={course} courseDetails={course.courseDetails} />
								}
							</Box>
						}
						{tab == 'enrollments' &&
							<Box>
								{enrollment != null ?
									(
										<Box>
											{course.enrollments.length != 1 && <Button onClick={() => navigate('/courses/' + course.id + '/enrollments')} variant="text" size="small" startIcon={<ChevronLeftIcon />}>Back to Enrollments</Button>}
											<ActivityTable enrollment={enrollment} />
										</Box>
									)
									:
									<Grid2 container sx={{ py: 2 }} spacing={4}>
										{_.chain(course.enrollments).orderBy('student.name').map((enrollment) => {
											enrollment.course = _.pick(course, ['id', 'name']);
											return (
												<Grid2 size={{ xs: 12, md: 6, lg: 4, xl: 3 }} key={enrollment.id}>
													<EnrollmentCard enrollment={enrollment} />
												</Grid2>
											)
										}).value()}
										{course.enrollments?.length == 0 && (
											<Stack justifyContent="center" sx={{ flex: 1, p: 8 }}>
												<Button color="primary" variant="contained" size="large" startIcon={<AddIcon sx={{ fontSize: '32px !important' }} />} sx={{ p: 8, fontSize: 32, lineHeight: 1 }} onClick={() => dialogs.open('enrollment', { course, courseId: course.id })}>Enroll a Student</Button>
											</Stack>
										)}
									</Grid2>
								}
							</Box>
						}
					</Grid2>
				</Grid2>
			</Paper>

			<SpeedDial sx={{ position: 'fixed', bottom: 10, right: (sessionData.drawerOpen ? 350 : 40), alignItems: 'end', '.MuiSpeedDial-actions': { alignItems: 'end', '.MuiFab-circular': { borderRadius: 2, width: 'auto' } } }} color="primary" icon={<SpeedDialIcon />} ariaLabel="">
				{course?.courseDetails?.length == 0 && <SpeedDialAction icon={<Typography sx={{ p: 2 }}>Add Coursework</Typography>} onClick={() => dialogs.open('courseDetailWizard', { course, courseId: course.id })} />}
				<SpeedDialAction icon={<Typography sx={{ p: 2 }}>Enroll a Student</Typography>} onClick={() => dialogs.open('enrollment', { course, courseId: course.id })} />
				<SpeedDialAction icon={<Typography sx={{ p: 2 }}>New Course</Typography>} onClick={() => dialogs.open('course', {})} />
			</SpeedDial>
		</Stack>
	);
}